import React, { useMemo } from "react";
import {
  Box,
  EntityGroup,
  ScrollArea,
  Text,
  type EntityGroupSize,
} from "@salestrak/ui";

import {
  entityToGroupItem,
  resolveEntityLabel,
  type EntityData,
  type EntityKind,
} from "#app/lib/entity-visuals";
import {
  presenceDotProps,
  usePresenceMap,
} from "#app/lib/data/presence-provider";

import { EntityVisual } from "./entity-visual";
import { useEntityDetailOptional } from "./entity-detail-provider";
import { EntitySelectionHover } from "./entity-selection-hover";
import type { Omittable } from "@salestrak/ui";

export type EntityGroupTagProps = Omittable<{
  entities: EntityData[];
  max?: number;
  size?: EntityGroupSize;
  variant?: "stack" | "expanded";
  title?: string;
  /** Default TRUE: clicking the group opens the list card (the only way to
   *  reach overflow entities on touch, where the hover card never fires).
   *  Pass false when the parent owns the click (popover/selector cells,
   *  clickable rows). */
  interactive?: boolean;
  /** Suppress the hover list card (e.g. while the owning picker is open and
   *  the trigger is frozen). */
  hoverDisabled?: boolean;
  /** Route list-row clicks here instead of the global detail dialog (e.g.
   *  selectors inside transient popovers the dialog would unmount). */
  onViewProfile?: OnViewProfile;
  /** Default TRUE: member avatars carry the live presence indicator. Pass
   *  false on dense surfaces where the dots read as noise (issue-card
   *  assignee triggers). */
  presence?: boolean;
}>;

// When a caller supplies this (e.g. a selector inside a transient popover that
// the global detail dialog would unmount), list rows route through it instead
// of the global detail.openEntity.
export type OnViewProfile = (kind: EntityKind, id: string) => void;

function EntityListRow({
  entity,
  onViewProfile,
}: Omittable<{
  entity: EntityData;
  onViewProfile?: OnViewProfile;
}>) {
  const detail = useEntityDetailOptional();
  const label = resolveEntityLabel(entity);
  const onClick = onViewProfile
    ? () => onViewProfile(entity.kind, entity.id)
    : detail
      ? () => detail.openEntity(entity.kind, entity.id)
      : undefined;

  return (
    <Box
      as={onClick ? "button" : "div"}
      direction="row"
      align="center"
      gap="sm"
      paddingX="sm"
      paddingY="xs"
      rounded="sm"
      hoverBackground={Boolean(onClick)}
      cursor={onClick ? "pointer" : undefined}
      fill="width"
      onClick={onClick}
    >
      <EntityVisual entity={entity} size="sm" />
      <Box shrink>
        <Text size="sm" truncate={1}>
          {label}
        </Text>
      </Box>
    </Box>
  );
}

export function EntityListCard({
  entities,
  title,
  onViewProfile,
}: Omittable<{
  entities: EntityData[];
  title?: string;
  onViewProfile?: OnViewProfile;
}>) {
  return (
    <Box direction="column" paddingY="xs" minWidth={200} maxWidth={280}>
      {title ? (
        <Box paddingX="sm" paddingY="2xs">
          <Text size="2xs" weight="medium" variant="muted">
            {title}
          </Text>
        </Box>
      ) : null}
      <ScrollArea style={{ maxHeight: 260 }}>
        {entities.map((entity, index) => (
          <EntityListRow
            key={`${entity.id}-${index}`}
            entity={entity}
            onViewProfile={onViewProfile}
          />
        ))}
      </ScrollArea>
    </Box>
  );
}

export function EntityGroupTag({
  entities,
  max = 3,
  size = "xs",
  variant = "stack",
  title,
  interactive = true,
  hoverDisabled,
  onViewProfile,
  presence: presenceEnabled = true,
}: EntityGroupTagProps) {
  // Presence dots on member avatars — the group renders through the library
  // EntityGroup (flat item primitives, not EntityVisual), so presence is
  // injected here as the semantic `presence` slot. Nothing for unknown
  // members or while the snapshot is in flight; Avatar itself suppresses the
  // dot for suspended/invited avatars.
  const presenceMap = usePresenceMap();
  const items = useMemo(
    () =>
      entities.map((entity) => {
        const item = entityToGroupItem(entity);
        if (!presenceEnabled || entity.kind !== "user") return item;
        const presence = presenceMap.get(entity.id);
        if (!presence) return item;
        return { ...item, presence: presenceDotProps(presence) };
      }),
    [entities, presenceMap, presenceEnabled],
  );

  if (items.length === 0) return null;

  return (
    <EntitySelectionHover
      entities={entities}
      title={title}
      disabled={hoverDisabled}
      clickOpensPreview={interactive}
      onViewProfile={onViewProfile}
    >
      <EntityGroup items={items} max={max} size={size} variant={variant} />
    </EntitySelectionHover>
  );
}
